/**
 * SFCC-11961 — Constructor.io beacon → JS SDK tracking cutover.
 *
 * The cnstrc.com beacon was the only sender of `session_start` and the
 * add-to-cart `conversion` (its customer config binds the PDP / PLP add
 * buttons), and nothing sent `input_focus` or `recommendation_view`. Behind the
 * BM `Constructor_SdkTrackingEnabled` switch (root loader →
 * `constructorio.config.sdkTrackingEnabled`) the beacon is not injected and
 * those events go through the shared SDK singleton instead. Everything here is
 * a no-op while the switch is off, so the beacon and the SDK never send the
 * same event.
 *
 * SFRA-rendered (proxied) pages run the same cutover from the same pref
 * (`org_hec` `js/constructor/client.js`); both storefronts share the
 * `session_start` dedupe key below and the SDK's cookie-stored session id, so a
 * shopper moving between them stays in one Constructor session.
 */
import { createContext, use } from "react";

import type ConstructorIOClient from "@constructor-io/constructorio-client-javascript";

import { safeLocalStorage, safeSessionStorage } from "~/lib/utils/safe-storage";
import { useCioClient } from "~/utils/constructor";

/** Shared with SFRA `org_hec/cartridge/client/default/js/constructor/client.js`. */
const SESSION_START_STORAGE_KEY = "hec_cnstrc_session_start";
/** Search term attributed to add-to-cart conversions (tracking-sheet rules). */
const SEARCH_TERM_STORAGE_KEY = "hec_cnstrc_search_term";
/** Constructor's value for a conversion with no attributable search. */
export const CIO_TERM_UNKNOWN = "TERM_UNKNOWN";

/**
 * Provided by root `App` from `constructorio.config.sdkTrackingEnabled`.
 * Defaults to off, so anything rendered outside it (tests, isolated trees)
 * keeps the beacon-era behaviour.
 */
export const CioSdkTrackingContext = createContext(false);

/** Whether this site tracks through the SDK instead of the beacon. */
export function useCioSdkTrackingEnabled(): boolean {
  return use(CioSdkTrackingContext);
}

/**
 * The shared SDK client, or `null` while the switch is off (beacon tracking),
 * during SSR, or before the deferred SDK chunk lands. For the events the
 * cutover moves onto the SDK only — events the SDK already sent before the
 * cutover keep using `useCioClient()`.
 */
export function useCioSdkTracker(): ConstructorIOClient | null {
  const cio = useCioClient();
  const enabled = useCioSdkTrackingEnabled();
  return enabled && cio ? cio : null;
}

/**
 * Sends `session_start` once per Constructor client/session pair. The SDK
 * never sends it itself (the beacon did, from its own identity module's
 * `session_is_new`, which the SDK does not expose), so the pair is remembered
 * in localStorage. Storage blocked → skip rather than send on every page.
 */
export function trackCioSessionStartOnce(cio: ConstructorIOClient): void {
  // `options` is typed private but is the SDK's own resolved identity (what
  // every request sends as `i` / `s`), so it is read rather than re-deriving
  // the ids from the `ConstructorioID_*` cookies.
  const { clientId, sessionId } = (
    cio as unknown as { options: { clientId?: string; sessionId?: number } }
  ).options;
  if (!clientId || !sessionId) return;
  const sessionKey = `${clientId}:${sessionId}`;
  if (safeLocalStorage.getItem(SESSION_START_STORAGE_KEY) === sessionKey) {
    return;
  }
  safeLocalStorage.setItem(SESSION_START_STORAGE_KEY, sessionKey);
  if (safeLocalStorage.getItem(SESSION_START_STORAGE_KEY) !== sessionKey) {
    return;
  }
  cio.tracker.trackSessionStart();
}

/**
 * Conversion search-term rules (Constructor tracking sheet, `trackConversion`):
 * store on search submit / search results load; clear on recommendation click,
 * browse page load and any unrelated page; resolve to `TERM_UNKNOWN` when
 * nothing is stored.
 */
export const cioSearchTerm = {
  store(term: string): void {
    const trimmed = term.trim();
    if (trimmed) safeSessionStorage.setItem(SEARCH_TERM_STORAGE_KEY, trimmed);
  },
  clear(): void {
    safeSessionStorage.removeItem(SEARCH_TERM_STORAGE_KEY);
  },
  resolve(): string {
    return (
      safeSessionStorage.getItem(SEARCH_TERM_STORAGE_KEY) || CIO_TERM_UNKNOWN
    );
  },
};
